import React, { useEffect, useRef, useCallback } from 'react';
import gsap from 'gsap';

import useScrollStore from '../../../../common/store/useScrollStore';
import { getThresholds } from '../../utils';
import { handleUpdate } from './utils';


const useAnimation = (
  outer: React.RefObject<THREE.Group>,
  inner: React.RefObject<THREE.Group>
): [
    rotateOut: React.MutableRefObject<GSAPTween | undefined>,
    rotateIn: React.MutableRefObject<GSAPTween | undefined>
  ] => {

  const lockScroll = useScrollStore(useCallback(state => state.lockScroll, []));
  const unlockScroll = useScrollStore(useCallback(state => state.unlockScroll, []));

  const rotateOut = useRef<GSAPTween>();
  const rotateIn = useRef<GSAPTween>();

  const thresholds = useRef(getThresholds());

  /**
   * Subscribe
   */
  useEffect(() => useScrollStore.subscribe(state => {
    if (outer.current) {
      const rotationFactor = 150;

      gsap.to(outer.current.rotation, {
        duration: 0,
        overwrite: true,
        y: state.position / rotationFactor,
        onUpdate: () => handleUpdate(outer.current!, thresholds.current, rotationFactor, false)
      });
    }
  }), []);

  /**
   * Resize
   */
  useEffect(() => {
    function handleResize() {
      thresholds.current = getThresholds();
    }

    window.addEventListener("resize", handleResize);
    return () => { window.removeEventListener("resize", handleResize) }
  }, []);

  /**
   * rotateOut & rotateIn
   */
  useEffect(() => {
    const duration = 0.75;

    rotateOut.current = gsap.fromTo(inner.current!.rotation, { y: 0 }, {
      duration,
      ease: "power2.in",
      y: Math.PI
    });

    rotateIn.current = gsap.fromTo(inner.current!.rotation, { y: Math.PI }, {
      duration,
      ease: "power2.out",
      y: 2 * Math.PI
    });

    rotateOut.current.eventCallback("onStart", () => lockScroll());
    rotateIn.current.eventCallback("onComplete", () => unlockScroll());

    return () => {
      rotateOut.current?.kill();
      rotateIn.current?.kill();
    }
  }, []);

  return [rotateOut, rotateIn];
}

export default useAnimation;